import * as React from 'react';
import AsyncSelect from 'react-select/async';
import { useSelector } from 'react-redux';

// Utils
import { t } from '@toolkit/util/i18n';
import { isOnewaySearch } from '@src/shared/src/util/search';
import { format } from 'date-fns';
// Constants
import { API_URL } from '@src/shared/src/const/api';
import { COSTCENTERS_RESULTS_PER_PAGE } from '@toolkit/const/app';
import { COST_CENTER_CATEGORIES } from '@src/shared/src/const/app';
// Models
import { OptionModel } from '@src/shared/src/models';
import { IRootState } from '@src/store';
import { WrappedFieldProps } from 'redux-form';

type CostCenterEntity = {
  id: number;
  name: string;
  disabled?: boolean;
};

type Props = WrappedFieldProps & {
  userIds: number[];
  mandatory: boolean;
  category: COST_CENTER_CATEGORIES;
  value?: OptionModel;
  onChange?: (option: OptionModel) => void;
};

export const SelectInputCostCenter: React.FC<Props> = (props) => {
  const { mandatory, category, userIds, meta, value, onChange, input } = props;

  const [isInitLoad, setIsInitLoad] = React.useState(true);
  const [initValue, setInitValue] = React.useState<OptionModel | undefined>(undefined);
  const search = useSelector((state: IRootState) => state.search.currentSearch);
  const validityDate: Date = isOnewaySearch(search) ? search.depAt : search.arrAt;

  const handleChange = (option: OptionModel) => {
    if (onChange) {
      onChange(option);
    } else {
      input.onChange(option);
    }
  };

  const buildQueryParams = (
    userIds: number[],
    searchInput: string,
    validityDate: Date,
    category: COST_CENTER_CATEGORIES,
  ): string => {
    const userIdsParam = userIds.map((userId) => `user_ids[]=${userId}`).join('&');
    const params = [
      userIdsParam,
      `name=${searchInput}`,
      `validity_date=${format(validityDate, 'yyyy-MM-dd')}`,
      `category=${category}`,
    ];
    return params.join('&');
  };

  const transformEntitiesToOptions = (entities: CostCenterEntity[]): OptionModel[] => {
    return entities.map((entity) => ({
      label: entity.name,
      value: String(entity.id),
    }));
  };

  const addFindMoreOption = (entities: CostCenterEntity[]): CostCenterEntity[] => {
    if (entities.length === COSTCENTERS_RESULTS_PER_PAGE) {
      return [
        ...entities,
        {
          id: 0,
          name: t('selectInputCostCenter.option.label.find_more'),
          disabled: true,
        },
      ];
    }
    return entities;
  };

  const loadOptions = (inputValue: string, callback: (options: OptionModel[]) => void) => {
    const queryParams = buildQueryParams(userIds, inputValue, validityDate, category);

    fetch(`${API_URL.COST_CENTER_ENTITIES}?${queryParams}`, {
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'include',
    })
      .then((response) => {
        return response.json();
      })
      .then((entities: CostCenterEntity[]) => {
        if (isInitLoad && entities.length === 1 && mandatory) {
          const singleEntity = transformEntitiesToOptions(entities)[0];
          setInitValue(singleEntity);
          setIsInitLoad(false);
          handleChange(singleEntity);
        }

        const entitiesWithFindMore = addFindMoreOption(entities);
        const options = transformEntitiesToOptions(entitiesWithFindMore);
        callback(options);
      });
  };

  const currentValue = value ? value : initValue || undefined;

  return (
    <div>
      <AsyncSelect
        {...props}
        className="react-select"
        classNamePrefix="react-select"
        defaultOptions={true}
        value={currentValue}
        onChange={handleChange}
        loadOptions={loadOptions}
        noResultsText={t('tripsConn.title.noResults')}
        placeholder={t('global.select')}
        isClearable={!mandatory}
      />
      {meta.error && <div className="form-error-message">{meta.error}</div>}
    </div>
  );
};
